import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";

import { BetSlipProvider } from "@/betslip/BetSlipProvider";
import { CapabilityProvider } from "@/capabilities/CapabilityProvider";
import { cashierAdapter } from "@/cashier/adapter";
import { CashierProvider } from "@/cashier/CashierProvider";
import { chatAdapter } from "@/chat/adapter";
import { ChatProvider } from "@/chat/ChatProvider";
import { RealtimeProvider } from "@/realtime/RealtimeProvider";
import {
  SessionProvider,
  type NativeProviderAuth,
} from "@/session/SessionProvider";
import { startNativeAuth } from "@/session/nativeAuth";
import {
  authenticateNativeApple,
  nativeAppleAvailable,
} from "@/session/nativeAppleAuth";
import { ThemeProvider } from "@/theme/ThemeProvider";

import { AppPreloader } from "./AppPreloader";

function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 15 * 60_000,
        retry: (failureCount, error) => {
          const status =
            typeof error === "object" && error !== null && "status" in error
              ? Number(error.status)
              : 0;
          if (status >= 400 && status < 500) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

const nativeProviderAuth: NativeProviderAuth = async (provider) => {
  if (
    provider.id === "apple" &&
    provider.nativeAppleAvailable &&
    (await nativeAppleAvailable())
  ) {
    return authenticateNativeApple();
  }
  await startNativeAuth(provider.id);
  return { status: "authenticated" };
};

export function RootProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <SessionProvider nativeProviderAuth={nativeProviderAuth}>
          <AppPreloader />
          {children}
        </SessionProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export function DestinationProviders({ children }: { children: ReactNode }) {
  return (
    <RealtimeProvider>
      <CashierProvider adapter={cashierAdapter}>
        <ChatProvider adapter={chatAdapter}>
          <CapabilityProvider>
            <BetSlipProvider>{children}</BetSlipProvider>
          </CapabilityProvider>
        </ChatProvider>
      </CashierProvider>
    </RealtimeProvider>
  );
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <RootProviders>
      <DestinationProviders>{children}</DestinationProviders>
    </RootProviders>
  );
}
